import React from 'react';
import { css } from '@emotion/react';
import { i18n_12_months_for_price_of_x__capital_months } from '@yi/i18n/phrases/i18n_12_months_for_price_of_x__capital_months';
import { i18n_2700_articles_podcasts_and_resources } from '@yi/i18n/phrases/i18n_2700_articles_podcasts_and_resources';
import { i18n_3000_on_demand_yoga_practices } from '@yi/i18n/phrases/i18n_3000_on_demand_yoga_practices';
import { i18n_300_courses_and_challenges } from '@yi/i18n/phrases/i18n_300_courses_and_challenges';
import { i18n_700_audio_and_video_meditations } from '@yi/i18n/phrases/i18n_700_audio_and_video_meditations';
import { i18n_annual_membership } from '@yi/i18n/phrases/i18n_annual_membership';
import { i18n_billed_annually } from '@yi/i18n/phrases/i18n_billed_annually';
import { i18n_billed_once_annually_at } from '@yi/i18n/phrases/i18n_billed_once_annually_at';
import { i18n_bonus_free_digital_practice_journal } from '@yi/i18n/phrases/i18n_bonus_free_digital_practice_journal';
import { i18n_discount_on_select_trainings__v2 } from '@yi/i18n/phrases/i18n_discount_on_select_trainings__v2';
import { i18n_per_month__v2 } from '@yi/i18n/phrases/i18n_per_month__v2';
import { i18n_save_x_percent } from '@yi/i18n/phrases/i18n_save_x_percent';
import { i18n_unlimited_access_to } from '@yi/i18n/phrases/i18n_unlimited_access_to';

import {
  PREMIUM_COURSES_ANNAUL_DISCOUNT,
  useAnnualPrice,
  useAnnualSavingsPercentage,
} from '~/src/common/membership';
import { useAnnualSignUpUrl } from '~/src/common/sign-up';
import { ErrorMessage } from '~/src/components/ErrorMessage';
import { useIsSSR } from '~/src/components/hooks/useIsSSR';
import { Bold } from '~/src/components/i18n';
import { useLocale } from '~/src/components/LocaleProvider';
import { paddingVariables, typography } from '~/src/styles/design-system';
import { breakpoints } from '~/src/styles/styled-mixins';
import { fontWeight } from '~/src/styles/styled-variables';

import { Benefit } from './Benefit';
import { commonStyles } from './common-styles';
import { ReactComponent as Mandala } from './mandala.svg';
import { PricePlaceholder } from './PricePlaceholder';
import { StartButton } from './StartButton';

const monthsEqualToAnnualPlan = 9;

const styles = {
  savings: css({
    color: 'var(--Pure-White, #FFF)',
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: fontWeight.bold,
    lineHeight: 'normal',
    padding: '5px',
    borderRadius: '2px',
    background: 'var(--Secondary, #2CB3C6)',
    [breakpoints.minTablet]: {
      fontSize: '20px',
      padding: '20px 40px',
    },
    [breakpoints.minDesktop]: {
      fontSize: '20px',
      padding: '23px 21px',
      borderRadius: '5px',
    },
  }),
  price: css({
    display: 'flex',
    flexDirection: 'column',
    gap: paddingVariables.p8,
  }),
  priceDetials: css(typography.bodyText, {
    color: 'rgba(0, 0, 0, 0.64)',
  }),
  mobilePriceDetials: css({
    display: 'block',
    [breakpoints.minDesktop]: {
      display: 'none',
    },
  }),
  desktopPriceDetials: css({
    display: 'none',
    [breakpoints.minDesktop]: {
      display: 'block',
    },
  }),
  container: css({
    [breakpoints.minDesktop]: {
      gridTemplateAreas: `'. .' '. .' 'benefits title' 'benefits .' 'benefits price' 'benefits .' 'benefits button'  '. .' '. .'`,
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: `${paddingVariables.p120} ${paddingVariables.p40} auto ${paddingVariables.p24} auto ${paddingVariables.p24} auto ${paddingVariables.p40} ${paddingVariables.p120}`,
      width: '100%',
    },
  }),
  title: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [breakpoints.minDesktop]: {
      rowGap: paddingVariables.p40,
      flexDirection: 'column-reverse',
    },
  }),
  mandala: css({
    gridColumn: '2/3',
  }),
};

export const AnnualPlan: React.FC<{ className?: string }> = props => {
  const { locale } = useLocale();
  const annualSavings = useAnnualSavingsPercentage();

  const { error, loading, price, pricePerMonth } = useAnnualPrice();
  const { href } = useAnnualSignUpUrl();

  const isSSR = useIsSSR();

  const errorMsg = isSSR ? null : error;

  return (
    <section css={[commonStyles.container, styles.container]} {...props}>
      <Mandala css={[commonStyles.mandala, styles.mandala]} />
      <h1 css={[commonStyles.title, styles.title]}>
        <span>{i18n_annual_membership(locale)}</span>
        <div css={styles.savings}>{i18n_save_x_percent(locale, { percentage: annualSavings })}</div>
      </h1>
      <div css={commonStyles.benefits}>
        <h2 css={commonStyles.benefitsTitle}>{i18n_unlimited_access_to(locale)}:</h2>
        <Benefit>{i18n_3000_on_demand_yoga_practices(locale)}</Benefit>
        <Benefit>{i18n_700_audio_and_video_meditations(locale)}</Benefit>
        <Benefit>{i18n_2700_articles_podcasts_and_resources(locale)}</Benefit>
        <Benefit>{i18n_300_courses_and_challenges(locale)}</Benefit>
        <Benefit>
          <b>{PREMIUM_COURSES_ANNAUL_DISCOUNT}</b> {i18n_discount_on_select_trainings__v2(locale)}
        </Benefit>
        <Benefit>
          <b>{i18n_12_months_for_price_of_x__capital_months(locale, monthsEqualToAnnualPlan)}</b>
        </Benefit>
        <Benefit>{i18n_bonus_free_digital_practice_journal(locale, { transformFn: Bold })}</Benefit>
      </div>
      <div css={[commonStyles.price, styles.price]}>
        {errorMsg ? (
          <ErrorMessage message={errorMsg} css={commonStyles.errorMsg} />
        ) : (
          <span>
            {loading ? (
              <PricePlaceholder uniqueKey="anual-monthly-price-placeholder" widthRatio={4} />
            ) : (
              pricePerMonth
            )}
            {` ${i18n_per_month__v2(locale)}`}
          </span>
        )}
        <div css={[styles.priceDetials, styles.mobilePriceDetials]}>
          {errorMsg ? (
            <ErrorMessage message={errorMsg} css={commonStyles.errorMsg} />
          ) : (
            <span>
              (
              {loading ? (
                <PricePlaceholder uniqueKey="anual-price-placeholder" widthRatio={4} />
              ) : (
                price
              )}
              {` ${i18n_billed_annually(locale)}`})
            </span>
          )}
        </div>
        <div css={[styles.priceDetials, styles.desktopPriceDetials]}>
          {errorMsg ? (
            <ErrorMessage message={errorMsg} css={commonStyles.errorMsg} />
          ) : (
            <span>
              {`${i18n_billed_once_annually_at(locale)} `}
              {loading ? (
                <PricePlaceholder uniqueKey="anual-price-desktop-placeholder" widthRatio={4} />
              ) : (
                price
              )}
            </span>
          )}
        </div>
      </div>
      <StartButton href={href} loading={loading} css={commonStyles.button} />
    </section>
  );
};
